import { useEffect, useRef, useState } from "react"
import { LazyMotion, domAnimation, motion } from "framer-motion"
import { IoClose } from "react-icons/io5"
import { IoIosArrowUp } from "react-icons/io"
import Modal from "components/Modal/Modal"
import VerificationComponent from "./AuthModalVerification"
import AuthModalLogin from "./AuthModalLogin"
import AuthModalSignup from "./AuthModalSignup"
import Button from "components/Form/Button"
import { useLang } from "hooks/useLang"
import AuthHeader from "components/page_components/auth/User/Signup/signupForm/header"

export type AuthModalSteps = "login" | "signup" | "ver"
export type AuthMode = "login" | "signup"

interface AuthModalProps {
    onClose: () => void
    onLoginSuccess?: () => void
    onSignupSuccess?: () => void
}

// ---------------- Hooks ----------------

function useLockBodyScroll() {
    useEffect(() => {
        const scrollY = window.scrollY
        Object.assign(document.body.style, {
            position: "fixed",
            top: `-${scrollY}px`,
            left: "0",
            right: "0",
            overflow: "hidden",
            width: "100%",
        })

        return () => {
            Object.assign(document.body.style, {
                position: "",
                top: "",
                left: "",
                right: "",
                overflow: "",
                width: "",
            })
            window.scrollTo(0, scrollY)
        }
    }, [])
}

function useContainerHeight(
    step: AuthModalSteps,
    contentRef:
        | React.RefObject<HTMLDivElement | null>
        | React.MutableRefObject<HTMLDivElement | null>,
    headerRef:
        | React.RefObject<HTMLDivElement | null>
        | React.MutableRefObject<HTMLDivElement | null>
) {
    const [containerHeight, setContainerHeight] = useState<number | null>(null)

    useEffect(() => {
        const compute = () => {
            const isDesktop = window.innerWidth >= 768
            const cap = Math.floor(
                window.innerHeight * (isDesktop ? 0.85 : 0.9)
            )
            const contentH = contentRef.current?.scrollHeight || 0
            const headerH = headerRef.current?.offsetHeight || 0
            setContainerHeight(Math.min(Math.max(contentH + headerH, 200), cap))
        }

        compute()
        const id = setTimeout(compute, 0)
        window.addEventListener("resize", compute)

        return () => {
            clearTimeout(id)
            window.removeEventListener("resize", compute)
        }
    }, [step])

    useEffect(() => {
        if (!contentRef.current) return
        const ro = new ResizeObserver(() => {
            const isDesktop = window.innerWidth >= 768
            const cap = Math.floor(
                window.innerHeight * (isDesktop ? 0.85 : 0.9)
            )
            const contentH = contentRef.current?.scrollHeight || 0
            const headerH = headerRef.current?.offsetHeight || 0
            setContainerHeight(Math.min(Math.max(contentH + headerH, 200), cap))
        })
        ro.observe(contentRef.current)
        return () => ro.disconnect()
    }, [])

    return containerHeight
}

function useIsIOS() {
    const [isIOS, setIsIOS] = useState(false)
    useEffect(() => {
        const ua = navigator.userAgent
        const platform = (navigator as any).platform || ""
        const maxTouch = (navigator as any).maxTouchPoints || 0
        const detected =
            /iPad|iPhone|iPod/.test(ua) ||
            /iPad|iPhone|iPod/.test(platform) ||
            (/Mac|iPad/.test(platform) && maxTouch > 1)
        setIsIOS(detected)
    }, [])
    return isIOS
}

// ---------------- Component ----------------

const AuthModal = ({
    onClose,
    onLoginSuccess,
    onSignupSuccess,
}: AuthModalProps) => {
    const { t } = useLang()
    const [step, setStep] = useState<AuthModalSteps>("login")
    const [authMode, setAuthMode] = useState<AuthMode>("login")
    const [authData, setAuthData] = useState<any>({})

    const contentRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const scrollerRef = useRef<HTMLDivElement>(null)

    useLockBodyScroll()
    const containerHeight = useContainerHeight(step, contentRef, headerRef)
    const isIOS = useIsIOS()

    const titles = {
        login: { title: "auth_modal_login_title", description: "" },
        signup: {
            title: "auth_modal_signup_title",
            description: "welcome_to_passtickets",
        },
        ver: { title: "", description: "" },
    }

    const { title, description } = titles[step]

    const handleVerificationSuccess = () => {
        onClose()
        if (authMode === "login") onLoginSuccess?.()
        if (authMode === "signup") onSignupSuccess?.()
    }

    return (
        <Modal
            isOpen
            Close={onClose}
            lockScroll
            closeOnOutsideClick={false}
            wrapperClassName="!z-[100]"
            className="fixed inset-0 flex items-center justify-center bg-black/40 p-4"
        >
            <motion.div
                ref={scrollerRef}
                className={`flex flex-col w-full max-w-[600px] 
                bg-white dark:bg-[#201750] rounded-3xl overflow-hidden
                ${isIOS ? "" : "modal-hide-native-scrollbar"}`}
                animate={{ height: containerHeight || undefined }}
                transition={{ type: "spring", stiffness: 240, damping: 28 }}
                initial={false}
                style={{ height: containerHeight || undefined }}
            >
                <div
                    ref={headerRef}
                    className="sticky top-0 z-10 flex justify-between items-center 
          px-5 py-5 bg-white dark:bg-[#201750] border-b 
          border-black/10 dark:border-white/10 shadow-sm"
                >
                    <div className="flex items-center gap-3">
                        {step === "ver" && authMode === "login" && (
                            <Button
                                className="bg-transparent hover:bg-transparent flex gap-x-1 
                justify-center items-center text-black dark:text-white"
                                onClick={() => setStep(authMode)}
                            >
                                <IoIosArrowUp className="ltr:-rotate-90 rtl:rotate-90 text-lg" />
                                <span className="text-md">{t("back")}</span>
                            </Button>
                        )}
                        {title && (
                            <span className="text-base md:text-lg font-medium">
                                {t(title)}
                            </span>
                        )}
                    </div>
                    <Button
                        className="bg-transparent hover:bg-transparent"
                        onClick={onClose}
                    >
                        <IoClose className="text-2xl" />
                    </Button>
                </div>

                {/* Body */}
                <div
                    className="w-full flex-1 min-h-0 overflow-y-auto"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    <div
                        ref={contentRef}
                        className="mx-auto w-full max-w-[500px] px-4 sm:px-6 pb-6"
                    >
                        <AuthHeader title="" description={description} />
                        <LazyMotion features={domAnimation}>
                            <motion.div
                                key={step}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2 }}
                            >
                                {step === "login" && (
                                    <AuthModalLogin
                                        step={step}
                                        setStep={setStep}
                                        setAuthData={setAuthData}
                                        setAuthMode={setAuthMode}
                                        onSwitchToSignup={() => {
                                            setStep("signup")
                                            setAuthMode("signup")
                                        }}
                                    />
                                )}
                                {step === "signup" && (
                                    <AuthModalSignup
                                        setStep={setStep}
                                        setAuthData={setAuthData}
                                        setAuthMode={setAuthMode}
                                        onSwitchToLogin={() => {
                                            setStep("login")
                                            setAuthMode("login")
                                        }}
                                        onSignupSuccess={
                                            handleVerificationSuccess
                                        }
                                    />
                                )}
                                {step === "ver" && (
                                    <VerificationComponent
                                        authData={authData}
                                        authMode={authMode}
                                        onVerificationSuccess={
                                            handleVerificationSuccess
                                        }
                                    />
                                )}
                            </motion.div>
                        </LazyMotion>
                    </div>
                </div>
            </motion.div>
        </Modal>
    )
}

export default AuthModal
